import React, { useState, useEffect, createContext, useContext, useRef, useMemo } from 'react';
import { HashRouter, Routes, Route, useNavigate, useParams, Link, useLocation } from 'react-router-dom';
import * as FiIcons from 'react-icons/fi';
import { dbAPI } from './db';

// --- Configuration ---
const GOOGLE_APPS_SCRIPT_WEB_APP_URL = "https://script.google.com/macros/s/AKfycbwTqV58nhr9p07SucKx0msnyIgousU5FjDVkCOExCJnv-chOcJlFhFKgusMjVkf0ko/exec";
const DEFAULT_CATEGORIES = ['Education', 'Business', 'Operations', 'Technical', 'Forms', 'Other'];
const AUTH_SESSION_DURATION = 30 * 60 * 1000; // 30 minutes
const APP_DATA_SCHEMA_VERSION = "2026-05-24-category-sync-v2";

// --- SafeIcon ---
const SafeIcon = ({ icon, name, className, ...props }) => {
  let IconComponent = icon || (name && FiIcons[`Fi${name}`]) || FiIcons.FiAlertTriangle;
  return <IconComponent className={className} {...props} />;
};

// --- Search Helpers ---
const normalizeSearchText = (val) => (val || '').toLowerCase().trim().replace(/\s+/g, ' ');

const stripHtmlForSearch = (html) => {
  if (!html) return '';
  const text = html.slice(0, 20000)
    .replace(/<(script|style)[^>]*>[\s\S]*?<\/\1>/gi, '')
    .replace(/<[^>]+>/g, ' ')
    .replace(/&nbsp;/g, ' ')
    .replace(/&[a-z0-9]+;/gi, ' ');
  return text.trim();
};

const fuzzyScore = (query, target) => {
  const q = normalizeSearchText(query);
  const t = normalizeSearchText(target);
  if (!q) return 0;
  if (t === q) return 1000;
  if (t.startsWith(q)) return 800;
  if (t.includes(q)) return 600;
  let score = 0;
  let qIdx = 0;
  for (let i = 0; i < t.length && qIdx < q.length; i++) {
    if (t[i] === q[qIdx]) { score += 10; qIdx++; } else { score -= 1; }
  }
  return qIdx === q.length ? Math.max(0, score) : 0;
};

// --- Contexts ---
const AppContext = createContext();
const ToastContext = createContext();

const ToastProvider = ({ children }) => {
  const [toasts, setToasts] = useState([]);
  const addToast = (message, type = 'info') => {
    const id = Date.now();
    setToasts(prev => [...prev, { id, message, type }]);
    setTimeout(() => setToasts(prev => prev.filter(t => t.id !== id)), 4000);
  };
  return (
    <ToastContext.Provider value={{ addToast }}>
      {children}
      <div className="fixed bottom-4 right-4 z-[100] flex flex-col gap-2">
        {toasts.map(t => (
          <div key={t.id} className={`px-4 py-3 rounded-lg shadow-xl text-sm font-medium flex items-center gap-2 animate-slide-in ${t.type === 'success' ? 'bg-green-600 text-white' : t.type === 'error' ? 'bg-red-600 text-white' : 'bg-blue-600 text-white'}`}>
            <SafeIcon name={t.type === 'success' ? 'CheckCircle' : t.type === 'error' ? 'AlertCircle' : 'Info'} />
            {t.message}
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
};

// --- Shared Components ---
const Modal = ({ isOpen, onClose, title, children, maxWidth = 'max-w-2xl' }) => {
  if (!isOpen) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm">
      <div className={`bg-white rounded-xl shadow-2xl w-full ${maxWidth} flex flex-col max-h-[90vh] overflow-hidden animate-scale-in`}>
        <div className="flex justify-between items-center p-5 border-b border-slate-100 bg-slate-50/50">
          <h2 className="text-lg font-bold text-slate-800">{title}</h2>
          <button onClick={onClose} className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-200/50 rounded-lg transition-all">
            <SafeIcon name="X" className="w-5 h-5" />
          </button>
        </div>
        <div className="p-6 overflow-y-auto flex-1 custom-scrollbar">{children}</div>
      </div>
    </div>
  );
};

// --- VaultAccessGate Component ---
const VaultAccessGate = ({ onUnlock }) => {
  const { addToast } = useContext(ToastContext);
  const [code, setCode] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (code.length !== 10) {
      addToast('Vault passcode must be exactly 10 digits.', 'error');
      return;
    }
    if (loading) return; // Prevent double-click
    setLoading(true);
    const result = await dbAPI.validatePasscode(GOOGLE_APPS_SCRIPT_WEB_APP_URL, code);
    setLoading(false);

    if (result.success) {
      addToast('Vault unlocked', 'success');
      onUnlock();
      setCode('');
    } else {
      if (result.code === 'BACKEND_NOT_READY') {
        addToast('The vault could not connect to its data store.', 'error');
      } else if (result.code === 'NETWORK_ERROR') {
        addToast(result.message, 'error');
      } else if (result.code === 'INVALID_RESPONSE') {
        addToast(result.message, 'error');
      } else {
        addToast(result.message || 'Incorrect passcode. Please try again.', 'error');
      }
    }
  };

  return (
    <div className="h-screen w-full flex items-center justify-center bg-slate-50">
      <div className="bg-white rounded-2xl shadow-2xl p-8 w-full max-w-md animate-scale-in">
        <div className="flex flex-col items-center mb-6">
          <div className="w-16 h-16 bg-blue-600 rounded-2xl flex items-center justify-center mb-4 shadow-lg shadow-blue-200">
            <SafeIcon name="Shield" className="w-8 h-8 text-white" />
          </div>
          <h1 className="font-black text-2xl text-slate-800 tracking-tight mb-2">BPA VAULT</h1>
          <p className="text-sm text-slate-500 text-center">Enter vault passcode to continue.</p>
        </div>
        <form onSubmit={handleSubmit} className="space-y-4">
          <input
            type="password"
            value={code}
            onChange={e => setCode(e.target.value.replace(/\D/g, '').slice(0, 10))}
            placeholder="••••••••••"
            className="w-full text-center text-2xl tracking-[0.5em] font-mono py-3 border-2 border-slate-200 rounded-xl focus:border-blue-500 outline-none transition-all"
            autoFocus
            maxLength={10}
          />
          <button
            disabled={loading || code.length !== 10}
            className="w-full py-3 bg-slate-900 text-white rounded-xl font-bold hover:bg-slate-800 disabled:opacity-50 transition-all flex items-center justify-center gap-2"
          >
            {loading ? <SafeIcon name="Loader" className="animate-spin" /> : 'Unlock Vault'}
          </button>
        </form>
      </div>
    </div>
  );
};

// --- PasscodeModal Component ---
const PasscodeModal = ({ isOpen, onClose, onVerified }) => {
  const { addToast } = useContext(ToastContext);
  const [code, setCode] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (code.length !== 10) {
      addToast('Editor passcode must be exactly 10 digits.', 'error');
      return;
    }
    if (loading) return; // Prevent double-click
    setLoading(true);
    const result = await dbAPI.validatePasscode(GOOGLE_APPS_SCRIPT_WEB_APP_URL, code);
    setLoading(false);

    if (result.success) {
      addToast('Editor access granted', 'success');
      onVerified();
      onClose();
      setCode('');
    } else {
      if (result.code === 'BACKEND_NOT_READY') {
        addToast('The vault could not connect to its data store.', 'error');
      } else if (result.code === 'NETWORK_ERROR') {
        addToast(result.message, 'error');
      } else if (result.code === 'INVALID_RESPONSE') {
        addToast(result.message, 'error');
      } else {
        addToast(result.message || 'Incorrect passcode. Please try again.', 'error');
      }
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Editor Auth" maxWidth="max-w-sm">
      <form onSubmit={handleSubmit} className="space-y-4">
        <p className="text-sm text-slate-600 text-center">Enter 10-digit editor passcode to continue.</p>
        <input
          type="password"
          value={code}
          onChange={e => setCode(e.target.value.replace(/\D/g, '').slice(0, 10))}
          placeholder="••••••••••"
          className="w-full text-center text-2xl tracking-[0.5em] font-mono py-3 border-2 border-slate-200 rounded-xl focus:border-blue-500 outline-none transition-all"
          autoFocus
        />
        <button
          disabled={loading || code.length !== 10}
          className="w-full py-3 bg-slate-900 text-white rounded-xl font-bold hover:bg-slate-800 disabled:opacity-50 transition-all flex items-center justify-center gap-2"
        >
          {loading ? <SafeIcon name="Loader" className="animate-spin" /> : 'Validate Access'}
        </button>
      </form>
    </Modal>
  );
};

// --- PageEditorModal Component ---
const PageEditorModal = ({ isOpen, onClose, pageToEdit = null, categories }) => {
  const { savePage } = useContext(AppContext);
  const { addToast } = useContext(ToastContext);
  const [formData, setFormData] = useState({ title: '', category: 'Other', description: '', tags: '', html: '' });

  useEffect(() => {
    if (pageToEdit) setFormData({ ...pageToEdit, category: pageToEdit.category || 'Other' });
    else setFormData({ title: '', category: 'Other', description: '', tags: '', html: '' });
  }, [pageToEdit, isOpen]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const newPage = {
      ...formData,
      id: pageToEdit ? pageToEdit.id : `page-${Date.now()}-${Math.random().toString(36).substr(2, 5)}`,
      createdAt: pageToEdit ? pageToEdit.createdAt : Date.now(),
      syncState: 'pending',
      syncError: '',
      lastSyncAttemptAt: 0,
      lastSyncedAt: 0
    };
    await savePage(newPage);
    addToast(pageToEdit ? 'Document details updated' : 'Document added', 'success');
    onClose();
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={pageToEdit ? "Edit Document Details" : "New Document"} maxWidth="max-w-4xl">
      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-1">
            <label className="text-xs font-bold text-slate-500 uppercase">Title *</label>
            <input type="text" value={formData.title} onChange={e => setFormData({ ...formData, title: e.target.value })} required className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none" />
          </div>
          <div className="space-y-1">
            <label className="text-xs font-bold text-slate-500 uppercase">Category *</label>
            <select value={formData.category} onChange={e => setFormData({ ...formData, category: e.target.value })} className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none">
              {categories.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
        </div>
        <div className="space-y-1">
          <label className="text-xs font-bold text-slate-500 uppercase">Description</label>
          <input type="text" value={formData.description} onChange={e => setFormData({ ...formData, description: e.target.value })} className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none" />
        </div>
        <div className="space-y-1">
          <label className="text-xs font-bold text-slate-500 uppercase">HTML Content *</label>
          <textarea value={formData.html} onChange={e => setFormData({ ...formData, html: e.target.value })} required rows={10} className="w-full px-4 py-2 border border-slate-200 rounded-lg font-mono text-sm focus:ring-2 focus:ring-blue-500 outline-none custom-scrollbar" placeholder="<!DOCTYPE html>..."></textarea>
        </div>
        <div className="flex justify-end gap-3 pt-4 border-t border-slate-100">
          <button type="button" onClick={onClose} className="px-4 py-2 text-slate-500 font-medium hover:bg-slate-100 rounded-lg">Cancel</button>
          <button type="submit" className="px-6 py-2 bg-blue-600 text-white rounded-lg font-bold hover:bg-blue-700 shadow-lg shadow-blue-200">Save Document</button>
        </div>
      </form>
    </Modal>
  );
};

// --- ManageCategoriesModal Component ---
const ManageCategoriesModal = ({ isOpen, onClose, categories }) => {
  const { pages, persistCategories, forceSync } = useContext(AppContext);
  const { addToast } = useContext(ToastContext);
  const [newCategoryName, setNewCategoryName] = useState('');
  const [loading, setLoading] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [renameValue, setRenameValue] = useState('');

  const getSessionPasscode = () => {
    const passcode = sessionStorage.getItem('BPA_SESSION_PASSCODE');
    const expiry = sessionStorage.getItem('BPA_SESSION_EXPIRY');
    if (passcode && expiry && Date.now() < parseInt(expiry, 10)) return passcode;
    return null;
  };

  const addCategory = async () => {
    const trimmed = newCategoryName.trim();
    if (!trimmed) { addToast('Category name cannot be empty', 'error'); return; }
    if (categories.some(c => c.toLowerCase() === trimmed.toLowerCase())) { addToast('Category already exists', 'error'); return; }
    const otherIdx = categories.indexOf('Other');
    const updated = [...categories];
    if (otherIdx !== -1) updated.splice(otherIdx, 0, trimmed); else updated.push(trimmed);
    await persistCategories(updated, { sync: true }, addToast);
    setNewCategoryName('');
    addToast(`Category "${trimmed}" added`, 'success');
  };

  const moveCategory = async (idx, direction) => {
    const cat = categories[idx];
    if (cat === 'Other') return;
    const targetIdx = idx + direction;
    if (targetIdx < 0 || targetIdx >= categories.length || categories[targetIdx] === 'Other') return;
    const updated = [...categories];
    const temp = updated[idx];
    updated[idx] = updated[targetIdx];
    updated[targetIdx] = temp;
    await persistCategories(updated, { sync: true }, addToast);
  };

  const renameCategory = async (oldName, newName) => {
    if (oldName === 'Other' || newName.trim() === '') { addToast('Cannot rename this category', 'error'); return false; }
    if (categories.some(c => c.toLowerCase() === newName.trim().toLowerCase())) { addToast('Category already exists', 'error'); return false; }
    const passcode = getSessionPasscode();
    if (!passcode) { addToast('Editor session expired', 'error'); return false; }
    setLoading(true);
    try {
      const pagesToUpdate = pages.filter(p => p.category === oldName && !p.deletedAt);
      for (const page of pagesToUpdate) {
        page.category = newName.trim();
        page.updatedAt = Date.now();
        await dbAPI.save(page);
      }
      await forceSync();
      const updated = categories.map(c => c === oldName ? newName.trim() : c);
      await persistCategories(updated, { sync: true }, addToast);
      addToast(`Renamed category and updated ${pagesToUpdate.length} documents`, 'success');
      return true;
    } catch (error) {
      console.error('Rename error:', error);
      addToast('Failed to rename category', 'error');
      return false;
    } finally { setLoading(false); }
  };

  const deleteCategory = async (cat) => {
    if (cat === 'Other') { addToast('"Other" category cannot be deleted', 'error'); return; }
    const isInUse = pages.some(p => p.category === cat && !p.deletedAt);
    if (isInUse) { addToast('This category is currently used by documents. Rename or move those documents first.', 'error'); return; }
    const updated = categories.filter(c => c !== cat);
    await persistCategories(updated, { sync: true }, addToast);
    addToast(`Category "${cat}" deleted`, 'success');
  };

  const saveCategories = async () => {
    setLoading(true);
    await persistCategories(categories, { sync: true }, addToast);
    setLoading(false);
    onClose();
    addToast('Categories saved and synced', 'success');
  };

  const startRename = (cat) => { setEditingCategory(cat); setRenameValue(cat); };
  const confirmRename = async () => {
    if (editingCategory && renameValue.trim() && renameValue !== editingCategory) {
      await renameCategory(editingCategory, renameValue);
    }
    setEditingCategory(null);
    setRenameValue('');
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Manage Categories" maxWidth="max-w-md">
      <div className="space-y-4">
        <div className="space-y-2">
          <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Current Categories</label>
          <div className="max-h-64 overflow-y-auto border border-slate-200 rounded-lg p-2 space-y-1 custom-scrollbar">
            {categories.map((cat, idx) => (
              <div key={cat} className="flex items-center justify-between p-2 bg-slate-50 rounded-md gap-2">
                {editingCategory === cat ? (
                  <div className="flex gap-2 flex-1">
                    <input type="text" value={renameValue} onChange={e => setRenameValue(e.target.value)} className="flex-1 px-2 py-1 text-sm border border-slate-200 rounded" autoFocus />
                    <button onClick={confirmRename} className="px-2 py-1 text-xs font-bold bg-green-600 text-white rounded">Save</button>
                    <button onClick={() => setEditingCategory(null)} className="px-2 py-1 text-xs font-bold bg-slate-300 text-white rounded">Cancel</button>
                  </div>
                ) : (
                  <>
                    <div className="flex items-center gap-2">
                      {cat !== 'Other' && (
                        <div className="flex flex-col gap-0.5">
                          <button disabled={idx === 0} onClick={() => moveCategory(idx, -1)} className="p-0.5 text-slate-400 hover:text-blue-600 disabled:opacity-20">
                            <SafeIcon name="ChevronUp" className="w-3 h-3" />
                          </button>
                          <button disabled={idx >= categories.length - 2} onClick={() => moveCategory(idx, 1)} className="p-0.5 text-slate-400 hover:text-blue-600 disabled:opacity-20">
                            <SafeIcon name="ChevronDown" className="w-3 h-3" />
                          </button>
                        </div>
                      )}
                      <span className="text-sm font-medium text-slate-700">{cat}</span>
                    </div>
                    <div className="flex gap-1">
                      {cat !== 'Other' && (
                        <>
                          <button onClick={() => startRename(cat)} className="px-2 py-1 text-xs font-bold text-blue-600 hover:bg-blue-50 rounded-md">Rename</button>
                          <button onClick={() => deleteCategory(cat)} className="px-2 py-1 text-xs font-bold text-red-600 hover:bg-red-50 rounded-md">Delete</button>
                        </>
                      )}
                    </div>
                  </>
                )}
              </div>
            ))}
          </div>
        </div>
        <div className="space-y-2">
          <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Add New Category</label>
          <div className="flex gap-2">
            <input type="text" value={newCategoryName} onChange={e => setNewCategoryName(e.target.value)} placeholder="Category name" className="flex-1 px-3 py-2 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 outline-none" onKeyPress={e => e.key === 'Enter' && addCategory()} />
            <button onClick={addCategory} disabled={!newCategoryName.trim()} className="px-4 py-2 bg-blue-600 text-white rounded-lg font-bold text-sm hover:bg-blue-700 disabled:opacity-50">Add</button>
          </div>
        </div>
        <div className="flex justify-end gap-3 pt-4 border-t border-slate-100">
          <button onClick={onClose} className="px-4 py-2 font-bold text-slate-500 hover:bg-slate-100 rounded-lg">Cancel</button>
          <button onClick={saveCategories} disabled={loading} className="px-6 py-2 bg-blue-600 text-white rounded-lg font-bold hover:bg-blue-700 transition-all flex items-center gap-2">
            {loading && <SafeIcon name="Loader" className="animate-spin" />}
            Save & Sync
          </button>
        </div>
      </div>
    </Modal>
  );
};

// --- Command Palette Component ---
const CommandPalette = ({ isOpen, onClose, pages, categories, onOpenPage, onSelectCategory }) => {
  const [query, setQuery] = useState('');
  const [highlightedIndex, setHighlightedIndex] = useState(0);
  const scrollRef = useRef(null);

  const searchableDocs = useMemo(() => pages.filter(p => !p.deletedAt), [pages]);

  const results = useMemo(() => {
    if (!query.trim()) {
      const recent = [...searchableDocs].sort((a, b) => b.updatedAt - a.updatedAt).slice(0, 5);
      const cats = categories.map(c => ({ type: 'category', name: c }));
      return [{ type: 'action', name: 'All documents', target: 'All' }, ...recent.map(p => ({ type: 'page', ...p })), ...cats];
    }
    const docResults = searchableDocs.map(p => {
      const titleScore = fuzzyScore(query, String(p.title));
      const catScore = fuzzyScore(query, String(p.category));
      const descScore = fuzzyScore(query, String(p.description));
      const tagsScore = fuzzyScore(query, String(p.tags || ''));
      const contentScore = fuzzyScore(query, stripHtmlForSearch(String(p.html || '')).slice(0, 2000));
      const maxScore = Math.max(titleScore * 1.5, catScore * 1.2, descScore, tagsScore, contentScore * 0.5);
      let matchLabel = '';
      if (titleScore * 1.5 >= maxScore) matchLabel = 'Matched title';
      else if (catScore * 1.2 >= maxScore) matchLabel = 'Matched category';
      else if (descScore >= maxScore) matchLabel = 'Matched description';
      else if (tagsScore >= maxScore) matchLabel = 'Matched tags';
      else matchLabel = 'Matched content';
      return { type: 'page', ...p, score: maxScore, matchLabel };
    }).filter(p => p.score > 0);
    const catResults = categories.filter(c => normalizeSearchText(String(c)).includes(normalizeSearchText(String(query)))).map(c => ({ type: 'category', name: c }));
    return [...catResults, ...docResults.sort((a, b) => b.score - a.score).slice(0, 20)];
  }, [query, searchableDocs, categories]);

  useEffect(() => { setHighlightedIndex(0); }, [query]);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!isOpen) return;
      if (e.key === 'ArrowDown') { e.preventDefault(); if (results.length > 0) setHighlightedIndex(prev => (prev + 1) % results.length); }
      else if (e.key === 'ArrowUp') { e.preventDefault(); if (results.length > 0) setHighlightedIndex(prev => (prev - 1 + results.length) % results.length); }
      else if (e.key === 'Enter') { e.preventDefault(); if (results.length > 0 && highlightedIndex >= 0 && highlightedIndex < results.length) handleSelect(results[highlightedIndex]); }
      else if (e.key === 'Escape') { onClose(); }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, results, highlightedIndex]);

  const handleSelect = (item) => {
    if (!item) return;
    if (item.type === 'page') onOpenPage(item.id);
    else if (item.type === 'category') onSelectCategory(item.name);
    else if (item.type === 'action') onSelectCategory(item.target);
    onClose();
    setQuery('');
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[200] flex items-start justify-center p-4 pt-[10vh] bg-slate-900/40 backdrop-blur-sm">
      <div className="bg-white w-full max-w-2xl rounded-2xl shadow-2xl overflow-hidden flex flex-col max-h-[70vh] border border-slate-200 animate-scale-in">
        <div className="p-4 border-b border-slate-100 flex items-center gap-3 bg-slate-50/50">
          <SafeIcon name="Search" className="w-5 h-5 text-slate-400" />
          <input autoFocus type="text" placeholder="Search documents, categories, or content..." className="flex-1 bg-transparent border-none outline-none text-lg text-slate-800 placeholder:text-slate-400" value={query} onChange={e => setQuery(e.target.value)} />
          <div className="flex items-center gap-1 px-1.5 py-0.5 bg-slate-200/50 rounded text-[10px] font-bold text-slate-500 uppercase tracking-tighter">ESC to close</div>
        </div>
        <div className="flex-1 overflow-y-auto custom-scrollbar p-2" ref={scrollRef}>
          {results.length > 0 ? (
            <div className="space-y-1">
              {results.map((item, idx) => {
                const isHighlighted = idx === highlightedIndex;
                if (item.type === 'page') {
                  return (
                    <div key={`page-${item.id}`} onClick={() => handleSelect(item)} onMouseEnter={() => setHighlightedIndex(idx)} className={`p-3 rounded-xl cursor-pointer transition-all flex items-start gap-4 ${isHighlighted ? 'bg-blue-50' : 'hover:bg-slate-50'}`}>
                      <div className={`w-10 h-10 rounded-lg flex items-center justify-center shrink-0 ${isHighlighted ? 'bg-blue-600' : 'bg-slate-100'}`}>
                        <SafeIcon name="FileText" className={`w-5 h-5 ${isHighlighted ? 'text-white' : 'text-slate-400'}`} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-0.5">
                          <h4 className={`font-bold text-sm truncate ${isHighlighted ? 'text-blue-900' : 'text-slate-800'}`}>{item.title}</h4>
                          <span className="text-[10px] font-black px-1.5 py-0.5 bg-slate-100 text-slate-500 rounded uppercase tracking-widest">{item.category}</span>
                        </div>
                        <p className="text-xs text-slate-500 line-clamp-1">{item.description || 'No description available'}</p>
                        <div className="flex items-center gap-3 mt-1.5">
                          <span className="text-[9px] font-bold text-slate-400 uppercase tracking-tight">Vault / {item.category} / {item.title}</span>
                          {query && <span className="text-[9px] font-black text-blue-500 uppercase tracking-widest">{item.matchLabel}</span>}
                        </div>
                      </div>
                      {isHighlighted && <SafeIcon name="CornerDownLeft" className="w-4 h-4 text-blue-400 mt-1" />}
                    </div>
                  );
                } else {
                  return (
                    <div key={`cat-${item.name}`} onClick={() => handleSelect(item)} onMouseEnter={() => setHighlightedIndex(idx)} className={`p-3 rounded-xl cursor-pointer transition-all flex items-center gap-4 ${isHighlighted ? 'bg-slate-100' : 'hover:bg-slate-50'}`}>
                      <div className="w-10 h-10 rounded-lg bg-slate-100 flex items-center justify-center shrink-0">
                        <SafeIcon name={item.type === 'action' ? 'Grid' : 'Hash'} className="w-5 h-5 text-slate-400" />
                      </div>
                      <div className="flex-1">
                        <h4 className="font-bold text-sm text-slate-800">{item.name}</h4>
                        <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest">{item.type === 'action' ? 'View all documents in library' : 'Filter library by category'}</p>
                      </div>
                      {isHighlighted && <SafeIcon name="CornerDownLeft" className="w-4 h-4 text-slate-400" />}
                    </div>
                  );
                }
              })}
            </div>
          ) : (
            <div className="py-12 text-center">
              <SafeIcon name="Search" className="w-8 h-8 text-slate-200 mx-auto mb-3" />
              <p className="text-slate-500 font-medium">No results found for "{query}"</p>
              <p className="text-xs text-slate-400 mt-1">Try searching for a different keyword or category.</p>
            </div>
          )}
        </div>
        <div className="p-3 bg-slate-50 border-t border-slate-100 flex justify-between items-center text-[10px] font-bold text-slate-400 uppercase tracking-widest">
          <div className="flex gap-4">
            <span className="flex items-center gap-1"><SafeIcon name="ArrowDown" className="w-3 h-3" /><SafeIcon name="ArrowUp" className="w-3 h-3" /> Navigate</span>
            <span className="flex items-center gap-1"><SafeIcon name="CornerDownLeft" className="w-3 h-3" /> Select</span>
          </div>
          <span>BPA Vault Command Palette</span>
        </div>
      </div>
    </div>
  );
};

// --- App Shell ---
const AppShell = ({ children }) => {
  const { pages, syncStatus, categories, selectedCategory, setSelectedCategory, forceSync, isVaultUnlocked, lockVault } = useContext(AppContext);
  const { addToast } = useContext(ToastContext);
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  const [isConfirmNavOpen, setIsConfirmNavOpen] = useState(false);
  const [isCommandOpen, setIsCommandOpen] = useState(false);
  const [isUnsyncedOpen, setIsUnsyncedOpen] = useState(false);
  const [pendingCategory, setPendingCategory] = useState(null);
  const location = useLocation();
  const navigate = useNavigate();
  const isViewingPage = location.pathname.startsWith('/page/');

  const handleGlobalKeys = (e) => {
    if (!isVaultUnlocked) return;
    const isMac = navigator.platform.toUpperCase().indexOf('MAC') >= 0;
    const isK = e.key.toLowerCase() === 'k';
    const modifier = isMac ? e.metaKey : e.ctrlKey;
    if (modifier && isK) { e.preventDefault(); setIsCommandOpen(prev => !prev); }
  };

  useEffect(() => {
    window.addEventListener('keydown', handleGlobalKeys);
    return () => window.removeEventListener('keydown', handleGlobalKeys);
  }, [isVaultUnlocked]);

  const handleSidebarNavigation = (cat) => {
    if (isViewingPage) { setPendingCategory(cat); setIsConfirmNavOpen(true); }
    else { setSelectedCategory(cat); navigate("/"); }
  };

  const confirmNavigation = () => {
    setSelectedCategory(pendingCategory);
    setIsConfirmNavOpen(false);
    setPendingCategory(null);
    navigate("/");
  };

  const getStatusColor = () => {
    switch (syncStatus) {
      case 'synced': return 'bg-green-500';
      case 'syncing': return 'bg-blue-500 animate-pulse';
      case 'error': return 'bg-red-500';
      default: return 'bg-slate-300';
    }
  };

  const getStatusText = () => {
    switch (syncStatus) {
      case 'synced': return 'Synced';
      case 'syncing': return 'Syncing...';
      case 'error': return 'Sync Failed';
      case 'offline': return 'Offline';
      default: return 'Offline';
    }
  };

  const unsyncedCount = pages.filter(p => p.syncState === 'pending' || p.syncState === 'syncing' || p.syncState === 'error').length;

  return (
    <div className="flex h-screen bg-slate-50 overflow-hidden">
      <aside className="w-64 bg-white border-r border-slate-200 flex flex-col shrink-0">
        <div className="p-6 border-b border-slate-100 flex items-center gap-3">
          <div className="w-10 h-10 bg-blue-600 rounded-xl flex items-center justify-center shadow-blue-200 shadow-lg">
            <SafeIcon name="Shield" className="w-6 h-6 text-white" />
          </div>
          <div>
            <h1 className="font-black text-lg text-slate-800 tracking-tight">BPA VAULT</h1>
            <p className="text-[10px] font-bold text-blue-600 uppercase tracking-widest leading-none">Internal Library</p>
          </div>
        </div>
        <nav className="flex-1 p-4 overflow-y-auto space-y-8 custom-scrollbar">
          <div>
            <button onClick={() => handleSidebarNavigation('All')} className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl font-bold transition-all ${selectedCategory === 'All' ? 'bg-slate-900 text-white shadow-lg shadow-slate-200' : 'bg-slate-100 text-slate-600'}`}>
              <SafeIcon name="Grid" className="w-5 h-5" />
              Library
            </button>
          </div>
          <div>
            <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-4 px-3">Filter by Category</p>
            <div className="space-y-1">
              {categories.map(cat => {
                const isActive = selectedCategory === cat;
                return (
                  <div key={cat} onClick={() => handleSidebarNavigation(cat)} className={`flex items-center justify-between px-3 py-2 rounded-lg cursor-pointer group transition-all ${isActive ? 'bg-blue-50 text-blue-600' : 'text-slate-600 hover:bg-slate-50'}`}>
                    <span className={`text-sm font-bold ${isActive ? 'text-blue-600' : 'group-hover:text-blue-600'}`}>{cat}</span>
                    <span className="text-[10px] font-black bg-slate-100 px-2 py-1 rounded-md text-slate-500">{pages.filter(p => !p.deletedAt && String(p.category || '').trim().toLowerCase() === String(cat || '').trim().toLowerCase()).length}</span>
                  </div>
                );
              })}
            </div>
          </div>
        </nav>
        <div className="p-4 border-t border-slate-100 bg-slate-50/50 space-y-2">
          <button onClick={() => setIsCommandOpen(true)} className="flex items-center gap-2 w-full px-3 py-2 text-[10px] font-black uppercase tracking-widest text-slate-600 bg-white border border-slate-200 rounded-lg hover:bg-slate-50 transition-all">
            <SafeIcon name="Search" className="w-3 h-3" />
            Search
            <span className="ml-auto opacity-40">Ctrl+K</span>
          </button>
          <button disabled={syncStatus === 'syncing'} onClick={() => forceSync()} className="flex items-center gap-2 w-full px-3 py-2 text-[10px] font-black uppercase tracking-widest text-blue-600 bg-white border border-blue-100 rounded-lg hover:bg-blue-50 transition-all disabled:opacity-50">
            <SafeIcon name="RefreshCw" className={`w-3 h-3 ${syncStatus === 'syncing' ? 'animate-spin' : ''}`} />
            {syncStatus === 'syncing' ? 'Syncing...' : 'Sync Now'}
          </button>
          <button onClick={() => setIsUnsyncedOpen(true)} className="flex items-center gap-2 w-full px-3 py-2 text-[10px] font-black uppercase tracking-widest text-slate-600 bg-white border border-slate-200 rounded-lg hover:bg-slate-50 transition-all">
            <SafeIcon name="AlertCircle" className="w-3 h-3" />
            Unsynced
            {unsyncedCount > 0 && <span className="ml-auto px-1.5 py-0.5 bg-red-500 text-white rounded-full text-[9px] font-black">{unsyncedCount}</span>}
          </button>
          <button onClick={() => setIsSettingsOpen(true)} className="flex items-center justify-between w-full px-3 py-2.5 text-xs font-bold text-slate-600 hover:bg-white hover:shadow-sm rounded-xl transition-all">
            <div className="flex items-center gap-2"><SafeIcon name="Settings" />Settings</div>
            <div className="flex items-center gap-1.5">
              <span className="text-[9px] uppercase tracking-tighter opacity-60">{getStatusText()}</span>
              <div className={`w-2 h-2 rounded-full ${getStatusColor()}`} />
            </div>
          </button>
          <button onClick={() => { lockVault(); }} className="flex items-center justify-between w-full px-3 py-2.5 text-xs font-bold text-slate-600 hover:bg-white hover:shadow-sm rounded-xl transition-all">
            <div className="flex items-center gap-2"><SafeIcon name="Lock" />Lock Vault</div>
          </button>
        </div>
      </aside>
      <main className="flex-1 flex flex-col overflow-hidden relative">
        {children}
        {isVaultUnlocked && (
          <button onClick={() => setIsCommandOpen(true)} className="md:hidden fixed bottom-6 right-6 w-14 h-14 bg-slate-900 text-white rounded-full shadow-2xl flex items-center justify-center z-40 active:scale-95 transition-transform">
            <SafeIcon name="Search" className="w-6 h-6" />
          </button>
        )}
      </main>
      <SettingsModal isOpen={isSettingsOpen} onClose={() => setIsSettingsOpen(false)} />
      <Modal isOpen={isConfirmNavOpen} onClose={() => setIsConfirmNavOpen(false)} title="Switch library view?" maxWidth="max-w-md">
        <div className="space-y-4">
          <div className="w-16 h-16 bg-blue-50 text-blue-600 rounded-full flex items-center justify-center mx-auto">
            <SafeIcon name="AlertCircle" className="w-8 h-8" />
          </div>
          <div className="text-center space-y-2">
            <p className="text-slate-700 font-bold">Close current document?</p>
            <p className="text-sm text-slate-500 px-4">This will close the current HTML page view. Any unsaved progress inside the embedded HTML may be lost.</p>
          </div>
          <div className="flex gap-3 pt-4 border-t border-slate-100">
            <button onClick={() => setIsConfirmNavOpen(false)} className="flex-1 py-3 text-slate-500 font-bold hover:bg-slate-100 rounded-xl transition-all">Cancel</button>
            <button onClick={confirmNavigation} className="flex-1 py-3 bg-blue-600 text-white font-bold rounded-xl shadow-lg shadow-blue-200 hover:bg-blue-700 transition-all">Switch View</button>
          </div>
        </div>
      </Modal>
      {isVaultUnlocked && (
        <CommandPalette isOpen={isCommandOpen} onClose={() => setIsCommandOpen(false)} pages={pages} categories={categories} onOpenPage={(id) => navigate(`/page/${id}`)} onSelectCategory={handleSidebarNavigation} />
      )}
      <UnsyncedFilesModal isOpen={isUnsyncedOpen} onClose={() => setIsUnsyncedOpen(false)} />
    </div>
  );
};

// --- Sync Status Indicator ---
const SyncStatusIndicator = ({ page }) => {
  const { syncStatus } = useContext(AppContext);
  const getStatusInfo = () => {
    const lastSyncAt = localStorage.getItem('BPA_LAST_SUCCESSFUL_SYNC_AT');
    const hasSessionSync = lastSyncAt && Date.now() - parseInt(lastSyncAt, 10) < 5 * 60 * 1000;
    switch (page.syncState) {
      case 'synced':
        if (syncStatus === 'synced' || hasSessionSync) return { icon: 'CheckCircle', color: 'text-green-500', bg: 'bg-green-100', tooltip: 'Synced' };
        else return { icon: 'HelpCircle', color: 'text-slate-400', bg: 'bg-slate-100', tooltip: 'Synced previously. Current session not verified.' };
      case 'pending': return { icon: 'Clock', color: 'text-amber-500', bg: 'bg-amber-100', tooltip: 'Pending sync' };
      case 'syncing': return { icon: 'Loader', color: 'text-blue-500', bg: 'bg-blue-100', tooltip: 'Syncing' };
      case 'error': return { icon: 'AlertCircle', color: 'text-red-500', bg: 'bg-red-100', tooltip: `Sync failed: ${page.syncError || 'Unknown error'}` };
      default: return { icon: 'HelpCircle', color: 'text-slate-400', bg: 'bg-slate-100', tooltip: 'Unknown sync status' };
    }
  };
  const statusInfo = getStatusInfo();
  return (
    <div className={`absolute top-3 right-3 w-6 h-6 rounded-full ${statusInfo.bg} flex items-center justify-center`} title={statusInfo.tooltip}>
      <SafeIcon name={statusInfo.icon} className={`w-3 h-3 ${statusInfo.color} ${statusInfo.icon === 'Loader' ? 'animate-spin' : ''}`} />
    </div>
  );
};

// --- Preview HTML Normaliser ---
const buildPreviewHtml = (html) => {
  if (!html) return '';
  let sanitized = html.replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, '');
  const previewStyles = `<style>body{margin:0;padding:0;overflow:hidden;}*{box-sizing:border-box;}</style>`;
  if (sanitized.includes('</head>')) sanitized = sanitized.replace('</head>', `${previewStyles}</head>`);
  else if (sanitized.includes('<html')) sanitized = sanitized.replace('<html', `<html><head>${previewStyles}</head>`);
  else sanitized = `${previewStyles}${sanitized}`;
  return sanitized;
};

// --- LazyPreviewFrame Component ---
const LazyPreviewFrame = ({ html, title }) => {
  const [isVisible, setIsVisible] = useState(false);
  const containerRef = useRef(null);

  useEffect(() => {
    if (!containerRef.current) return;
    if ('IntersectionObserver' in window) {
      const observer = new IntersectionObserver((entries) => {
        entries.forEach(entry => { if (entry.isIntersecting) { setIsVisible(true); observer.disconnect(); } });
      }, { rootMargin: '50px' });
      observer.observe(containerRef.current);
      return () => observer.disconnect();
    } else { setIsVisible(true); }
  }, []);

  if (!html) {
    return (
      <div className="w-full h-32 md:h-36 bg-slate-100 rounded-t-xl flex items-center justify-center">
        <p className="text-slate-400 text-sm">No preview available</p>
      </div>
    );
  }

  return (
    <div ref={containerRef} className="w-full h-32 md:h-36 bg-white border-b border-slate-200 overflow-hidden relative">
      {isVisible ? (
        <div className="absolute inset-0" style={{ transform: 'scale(0.28)', transformOrigin: 'top left', width: '357%', height: '357%' }}>
          <iframe srcDoc={buildPreviewHtml(html)} title={`Preview: ${title}`} sandbox="" scrolling="no" className="w-full h-full pointer-events-none" />
        </div>
      ) : (
        <div className="w-full h-full flex items-center justify-center bg-slate-50">
          <div className="text-center">
            <SafeIcon name="Eye" className="w-8 h-8 text-slate-300 mx-auto mb-1" />
            <p className="text-xs text-slate-400">Preview loading</p>
          </div>
        </div>
      )}
    </div>
  );
};

// --- Unsynced Files Modal ---
const UnsyncedFilesModal = ({ isOpen, onClose }) => {
  const { pages, requestAuth, forceSync, deletePage } = useContext(AppContext);
  const { addToast } = useContext(ToastContext);
  const unsyncedFiles = pages.filter(p => p.syncState === 'pending' || p.syncState === 'syncing' || p.syncState === 'error');

  const handleDownload = (page) => {
    const blob = new Blob([page.html], { type: 'text/html' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `${page.title.toLowerCase().replace(/\s+/g, '-')}.html`;
    a.click();
    addToast('File downloaded', 'success');
  };

  const handleRetry = () => { forceSync(addToast); onClose(); };
  const handleDelete = async (page) => { requestAuth(async () => { await deletePage(page.id); addToast('File deleted', 'success'); }); };

  if (!isOpen) return null;

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Unsynced Files" maxWidth="max-w-2xl">
      <div className="space-y-4">
        {unsyncedFiles.length === 0 ? (
          <div className="text-center py-8">
            <SafeIcon name="CheckCircle" className="w-12 h-12 text-green-500 mx-auto mb-3" />
            <p className="text-slate-600 font-medium">All visible files are synced.</p>
          </div>
        ) : (
          <div className="space-y-3">
            {unsyncedFiles.map(page => (
              <div key={page.id} className="border border-slate-200 rounded-lg p-4 hover:bg-slate-50 transition-all">
                <div className="flex items-start justify-between mb-2">
                  <div className="flex-1">
                    <h4 className="font-bold text-slate-800 truncate">{page.title}</h4>
                    <p className="text-sm text-slate-500">{page.category}</p>
                    {page.syncError && <p className="text-xs text-red-600 mt-1">Error: {page.syncError}</p>}
                  </div>
                  <div className="flex gap-2">
                    <button onClick={() => handleDownload(page)} className="p-2 text-slate-500 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-all" title="Download HTML"><SafeIcon name="Download" className="w-4 h-4" /></button>
                    <button onClick={handleRetry} className="p-2 text-slate-500 hover:text-green-600 hover:bg-green-50 rounded-lg transition-all" title="Retry Sync"><SafeIcon name="RefreshCw" className="w-4 h-4" /></button>
                    <button onClick={() => handleDelete(page)} className="p-2 text-slate-500 hover:text-red-600 hover:bg-red-50 rounded-lg transition-all" title="Delete Local Copy"><SafeIcon name="Trash2" className="w-4 h-4" /></button>
                  </div>
                </div>
                <div className="flex items-center gap-2 text-xs text-slate-400">
                  <SafeIcon name="Clock" className="w-3 h-3" />
                  <span>Updated: {new Date(page.updatedAt).toLocaleString()}</span>
                </div>
              </div>
            ))}
          </div>
        )}
        <div className="flex justify-end gap-3 pt-4 border-t border-slate-100">
          <button onClick={handleRetry} className="px-4 py-2 bg-blue-600 text-white rounded-lg font-bold hover:bg-blue-700">Retry All Sync</button>
          <button onClick={onClose} className="px-4 py-2 text-slate-500 font-bold hover:bg-slate-100 rounded-lg">Close</button>
        </div>
      </div>
    </Modal>
  );
};

// --- Dashboard ---
const Dashboard = () => {
  const { pages, requestAuth, savePage, deletePage, categories, selectedCategory, loadData, forceSync, viewMode, setViewMode } = useContext(AppContext);
  const { addToast } = useContext(ToastContext);
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [isEditorOpen, setIsEditorOpen] = useState(false);
  const [editingPage, setEditingPage] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const [bulkUploadFiles, setBulkUploadFiles] = useState([]);
  const [bulkCategory, setBulkCategory] = useState('Other');
  const [deleteTarget, setDeleteTarget] = useState(null);

  useEffect(() => {
    if (window.__editPage) { setEditingPage(window.__editPage); setIsEditorOpen(true); window.__editPage = null; }
  }, []);

  const filteredPages = pages.filter(p => {
    const matchesSearch = (p.title + p.description + (p.tags || '')).toLowerCase().includes(search.toLowerCase());
    const matchesCategory = selectedCategory === 'All' || String(p.category || '').trim().toLowerCase() === String(selectedCategory || '').trim().toLowerCase();
    return matchesSearch && matchesCategory;
  });

  const handleAddClick = () => { requestAuth(() => { setEditingPage(null); setIsEditorOpen(true); }); };
  const handleEdit = (e, page) => { e.stopPropagation(); requestAuth(() => { setEditingPage(page); setIsEditorOpen(true); }); };
  const handleDelete = (e, page) => { e.stopPropagation(); setDeleteTarget(page); };
  const confirmDelete = () => { requestAuth(async () => { await deletePage(deleteTarget.id); addToast('Document hidden from library', 'success'); setDeleteTarget(null); }); };

  const onDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    const files = Array.from(e.dataTransfer.files).filter(f => f.name.endsWith('.html'));
    if (files.length > 0) { requestAuth(() => setBulkUploadFiles(files)); }
    else { addToast('Only .html files are supported', 'error'); }
  };

  const processBulkUpload = async () => {
    for (let i = 0; i < bulkUploadFiles.length; i++) {
      const file = bulkUploadFiles[i];
      const text = await file.text();
      const title = file.name.replace('.html', '').replace(/[-_]/g, ' ');
      const page = {
        id: `page-${Date.now()}-${i}-${Math.random().toString(36).slice(2, 8)}`,
        title, category: bulkCategory, description: `Imported file: ${file.name}`, html: text,
        createdAt: Date.now(), updatedAt: Date.now(), syncState: 'pending', syncError: '', lastSyncAttemptAt: 0, lastSyncedAt: 0
      };
      await dbAPI.save(page);
    }
    if (loadData) await loadData();
    if (forceSync) await forceSync(addToast);
    addToast(`Uploaded ${bulkUploadFiles.length} documents`, 'success');
    setBulkUploadFiles([]);
  };

  return (
    <div className={`flex-1 flex flex-col h-full overflow-hidden transition-colors ${isDragging ? 'bg-blue-50' : 'bg-slate-50'}`} onDragOver={e => { e.preventDefault(); setIsDragging(true); }} onDragLeave={() => setIsDragging(false)} onDrop={onDrop}>
      <header className="bg-white border-b border-slate-200 px-8 py-6 flex items-center justify-between shrink-0">
        <div>
          <h2 className="text-2xl font-black text-slate-800 tracking-tight">Vault Library</h2>
          <p className="text-sm font-medium text-slate-500">Drag & drop HTML files here to upload</p>
        </div>
        <div className="flex items-center gap-4">
          <div className="relative">
            <SafeIcon name="Search" className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input type="text" placeholder="Filter vault..." value={search} onChange={e => setSearch(e.target.value)} className="pl-10 pr-4 py-2 border border-slate-200 rounded-xl text-sm w-64 focus:ring-2 focus:ring-blue-500 outline-none bg-slate-50/50" />
          </div>
          <select value={viewMode} onChange={(e) => { const newMode = e.target.value; setViewMode(newMode); localStorage.setItem('BPA_CARD_VIEW_MODE', newMode); }} className="px-3 py-2 border border-slate-200 rounded-xl text-sm bg-white font-medium">
            <option value="preview">Preview</option>
            <option value="cards">Cards</option>
          </select>
          <button onClick={handleAddClick} className="flex items-center gap-2 px-5 py-2.5 bg-blue-600 text-white rounded-xl font-bold hover:bg-blue-700 shadow-lg shadow-blue-200 transition-all">
            <SafeIcon name="Plus" />
            New Document
          </button>
        </div>
      </header>
      <div className="flex-1 overflow-y-auto p-8 custom-scrollbar">
        {filteredPages.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {filteredPages.map(page => (
              <div key={page.id} onClick={() => navigate(`/page/${page.id}`)} className="relative bg-white rounded-2xl border border-slate-200 overflow-hidden hover:shadow-xl hover:border-blue-400/50 transition-all cursor-pointer group flex flex-col h-64">
                {viewMode === 'preview' && <LazyPreviewFrame html={page.html} title={page.title} />}
                <SyncStatusIndicator page={page} />
                <div className="p-6 flex-1">
                  <div className="flex justify-between items-start mb-4">
                    <span className="text-[10px] font-black px-2 py-1 bg-slate-100 text-slate-600 rounded-md uppercase tracking-widest">{page.category}</span>
                    <div className="opacity-0 group-hover:opacity-100 transition-opacity flex gap-1">
                      <button onClick={(e) => handleEdit(e, page)} className="p-2 bg-slate-100 text-slate-500 hover:text-blue-600 rounded-lg"><SafeIcon name="Edit3" /></button>
                      <button onClick={(e) => handleDelete(e, page)} className="p-2 bg-slate-100 text-slate-500 hover:text-red-600 rounded-lg"><SafeIcon name="Trash2" /></button>
                    </div>
                  </div>
                  <h3 className="font-bold text-lg text-slate-800 mb-2 line-clamp-2 leading-snug">{page.title}</h3>
                  <p className="text-xs font-medium text-slate-500 line-clamp-3 leading-relaxed">{page.description}</p>
                </div>
                <div className="px-6 py-4 bg-slate-50/80 border-t border-slate-100 flex justify-between items-center text-[10px] font-black text-slate-400 uppercase tracking-tighter">
                  <span>{new Date(page.updatedAt).toLocaleDateString()}</span>
                  <SafeIcon name="ChevronRight" className="text-slate-300 group-hover:text-blue-500 transition-colors" />
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="h-full flex flex-col items-center justify-center text-slate-400">
            <div className="w-20 h-20 bg-slate-100 rounded-full flex items-center justify-center mb-6">
              <SafeIcon name="Folder" className="w-10 h-10" />
            </div>
            <p className="text-lg font-bold text-slate-600">{selectedCategory === 'All' ? 'No documents in vault' : `No documents in ${selectedCategory} category`}</p>
            <p className="text-sm">{selectedCategory === 'All' ? 'Drag HTML files here or use the "New Document" button.' : 'Add or upload an HTML file to this category.'}</p>
          </div>
        )}
      </div>
      <PageEditorModal isOpen={isEditorOpen} onClose={() => setIsEditorOpen(false)} pageToEdit={editingPage} categories={categories} />
      <Modal isOpen={bulkUploadFiles.length > 0} onClose={() => setBulkUploadFiles([])} title="Categorize Uploads" maxWidth="max-w-md">
        <p className="text-sm text-slate-600 mb-4">Assign a category for {bulkUploadFiles.length} file(s):</p>
        <select value={bulkCategory} onChange={e => setBulkCategory(e.target.value)} className="w-full px-4 py-2 border border-slate-200 rounded-xl mb-6">
          {categories.map(c => <option key={c} value={c}>{c}</option>)}
        </select>
        <div className="flex justify-end gap-3">
          <button onClick={() => setBulkUploadFiles([])} className="px-4 py-2 font-bold text-slate-500">Cancel</button>
          <button onClick={processBulkUpload} className="px-6 py-2 bg-blue-600 text-white rounded-xl font-bold shadow-lg shadow-blue-200">Start Upload</button>
        </div>
      </Modal>
      <Modal isOpen={!!deleteTarget} onClose={() => setDeleteTarget(null)} title="Hide Document" maxWidth="max-w-md">
        <div className="text-center py-4">
          <div className="w-16 h-16 bg-red-50 text-red-600 rounded-full flex items-center justify-center mx-auto mb-4">
            <SafeIcon name="AlertTriangle" className="w-8 h-8" />
          </div>
          <p className="text-slate-700 font-medium mb-1">Delete "{deleteTarget?.title}"?</p>
          <p className="text-xs text-slate-500 px-4">This will hide the document from the library and can be restored from the backend if required.</p>
        </div>
        <div className="flex justify-center gap-3 pt-4">
          <button onClick={() => setDeleteTarget(null)} className="px-6 py-2 font-bold text-slate-500 hover:bg-slate-100 rounded-xl">Cancel</button>
          <button onClick={confirmDelete} className="px-6 py-2 bg-red-600 text-white rounded-xl font-bold shadow-lg shadow-red-200">Confirm Deletion</button>
        </div>
      </Modal>
    </div>
  );
};

// --- Page Viewer ---
const PageView = () => {
  const { id } = useParams();
  const { pages, requestAuth } = useContext(AppContext);
  const { addToast } = useContext(ToastContext);
  const navigate = useNavigate();
  const iframeRef = useRef(null);
  const page = pages.find(p => p.id === id);

  if (!page) {
    return (
      <div className="h-full flex flex-col items-center justify-center bg-slate-50">
        <SafeIcon name="FileX" className="w-16 h-16 text-slate-300 mb-4" />
        <h2 className="text-xl font-black text-slate-800">404 Vault Not Found</h2>
        <button onClick={() => navigate('/')} className="mt-4 px-6 py-2 bg-slate-900 text-white rounded-xl font-bold">Return to Library</button>
      </div>
    );
  }

  const actions = [
    { icon: 'Link', label: 'Link', onClick: () => { navigator.clipboard.writeText(window.location.href); addToast('Share link copied', 'success'); } },
    { icon: 'Code', label: 'HTML', onClick: () => { navigator.clipboard.writeText(page.html); addToast('HTML source copied', 'success'); } },
    { icon: 'Printer', label: 'Print', onClick: () => iframeRef.current?.contentWindow.print() },
    { icon: 'Download', label: 'Save', onClick: () => { const blob = new Blob([page.html], { type: 'text/html' }); const a = document.createElement('a'); a.href = URL.createObjectURL(blob); a.download = `${page.title.toLocaleLowerCase().replace(/\s+/g, '-')}.html`; a.click(); } },
    { icon: 'ExternalLink', label: 'Full', onClick: () => { const blob = new Blob([page.html], { type: 'text/html' }); window.open(URL.createObjectURL(blob), '_blank'); } },
    { icon: 'Edit3', label: 'Edit', onClick: () => { requestAuth(() => { window.__editPage = page; navigate('/'); }); } }
  ];

  return (
    <div className="h-screen flex flex-col bg-white overflow-hidden">
      <header className="bg-white border-b border-slate-200 px-4 py-2 flex items-center justify-between shrink-0">
        <div className="flex items-center gap-3">
          <button onClick={() => navigate('/')} className="p-1.5 hover:bg-slate-100 rounded-lg text-slate-500"><SafeIcon name="ArrowLeft" /></button>
          <div>
            <h2 className="font-bold text-slate-800 leading-none text-sm">{page.title}</h2>
            <p className="text-[10px] font-black text-blue-600 uppercase tracking-widest">{page.category}</p>
          </div>
        </div>
        <div className="flex items-center gap-1">
          {actions.map(a => (
            <button key={a.label} onClick={a.onClick} className="flex items-center gap-1 px-2 py-1 text-[10px] font-bold text-slate-600 hover:bg-slate-100 rounded-md uppercase tracking-widest transition-all">
              <SafeIcon name={a.icon} className="w-3 h-3" />
              <span className="hidden md:inline">{a.label}</span>
            </button>
          ))}
        </div>
      </header>
      <div className="flex-1 w-full overflow-hidden">
        <iframe ref={iframeRef} srcDoc={page.html} title={page.title} sandbox="allow-scripts allow-same-origin allow-forms allow-popups allow-modals" className="w-full h-full border-0" />
      </div>
    </div>
  );
};

// --- Settings Modal ---
const SettingsModal = ({ isOpen, onClose }) => {
  const { syncStatus, forceSync, exportBackup, importBackup, requestAuth, categories, safeRefreshFromCloud, lockVault } = useContext(AppContext);
  const { addToast } = useContext(ToastContext);
  const [isManageCategoriesOpen, setIsManageCategoriesOpen] = useState(false);
  const fileRef = useRef();

  const handleManageCategories = () => { requestAuth(() => { setIsManageCategoriesOpen(true); }); };

  const getLastSyncText = () => {
    const lastSync = localStorage.getItem('BPA_LAST_SUCCESSFUL_SYNC_AT');
    if (!lastSync) return 'Never';
    const diff = Date.now() - parseInt(lastSync, 10);
    if (diff < 60000) return 'Just now';
    if (diff < 3600000) return `${Math.floor(diff / 60000)} min ago`;
    return new Date(parseInt(lastSync, 10)).toLocaleString();
  };

  const getStatusText = () => {
    switch (syncStatus) {
      case 'synced': return 'Synced';
      case 'syncing': return 'Syncing...';
      case 'error': return 'Sync Failed';
      default: return 'Offline';
    }
  };

  return (
    <>
      <Modal isOpen={isOpen} onClose={onClose} title="Vault Settings" maxWidth="max-w-md">
        <div className="space-y-6">
          {/* Read-only sync info */}
          <div className="space-y-3">
            <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Cloud Sync</label>
            <div className="bg-slate-50 rounded-xl p-4 space-y-2">
              <div className="flex items-center gap-2 text-sm text-slate-600">
                <SafeIcon name="Cloud" className="w-4 h-4 text-blue-500" />
                <span className="font-medium">Cloud sync is configured automatically</span>
              </div>
              <div className="flex items-center justify-between text-xs text-slate-500">
                <span>Status</span>
                <span className="font-bold text-slate-700">{getStatusText()}</span>
              </div>
              <div className="flex items-center justify-between text-xs text-slate-500">
                <span>Last successful sync</span>
                <span className="font-bold text-slate-700">{getLastSyncText()}</span>
              </div>
              <button onClick={() => forceSync(addToast)} disabled={syncStatus === 'syncing'} className="w-full py-2 bg-blue-600 text-white rounded-xl font-bold text-xs uppercase hover:bg-blue-700 disabled:opacity-50 flex items-center justify-center gap-2">
                {syncStatus === 'syncing' ? <SafeIcon name="Loader" className="animate-spin w-3 h-3" /> : <SafeIcon name="RefreshCw" className="w-3 h-3" />}
                Sync Now
              </button>
            </div>
          </div>

          <div className="pt-4 border-t border-slate-100">
            <button onClick={handleManageCategories} className="w-full py-2 border border-slate-200 rounded-xl font-bold text-xs uppercase text-slate-600 hover:bg-slate-50 flex items-center justify-center gap-2">
              <SafeIcon name="Tag" />
              Manage Categories
            </button>
          </div>

          <div className="pt-4 border-t border-slate-100 flex gap-2">
            <button onClick={exportBackup} className="flex-1 py-2 border border-slate-200 rounded-xl font-bold text-xs uppercase text-slate-600 hover:bg-slate-50">Export DB</button>
            <button onClick={() => fileRef.current.click()} className="flex-1 py-2 border border-slate-200 rounded-xl font-bold text-xs uppercase text-slate-600 hover:bg-slate-50">Import DB</button>
            <button onClick={() => { safeRefreshFromCloud(false, addToast); onClose(); }} className="flex-1 py-2 bg-blue-50 border border-blue-200 text-blue-600 rounded-xl font-bold text-xs uppercase hover:bg-blue-100">Refresh from Cloud</button>
            <input type="file" hidden ref={fileRef} accept=".json" onChange={async (e) => { const file = e.target.files[0]; if (file) { const data = JSON.parse(await file.text()); await importBackup(data); addToast('Database merged', 'success'); onClose(); } }} />
          </div>

          <div className="pt-4 border-t border-slate-100">
            <button onClick={() => { lockVault(); onClose(); }} className="w-full py-2 border border-red-200 bg-red-50 text-red-600 rounded-xl font-bold text-xs uppercase hover:bg-red-100 flex items-center justify-center gap-2">
              <SafeIcon name="Lock" />
              Lock Vault
            </button>
          </div>
        </div>
      </Modal>
      <ManageCategoriesModal isOpen={isManageCategoriesOpen} onClose={() => setIsManageCategoriesOpen(false)} categories={categories} />
    </>
  );
};

// --- Main App & Provider ---
export default function App() {
  const [pages, setPages] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [syncStatus, setSyncStatus] = useState('offline');
  const [auth, setAuth] = useState({ token: null, expiry: 0 });
  const [isAuthModalOpen, setIsAuthModalOpen] = useState(false);
  const [pendingAction, setPendingAction] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [isVaultUnlocked, setIsVaultUnlocked] = useState(false);

  const [viewMode, setViewMode] = useState(() => localStorage.getItem('BPA_CARD_VIEW_MODE') || 'preview');

  // Sync locking refs
  const syncInProgressRef = useRef(false);
  const syncQueuedRef = useRef(false);
  const vaultUnlockedRef = useRef(false);
  const lastSyncTriggerRef = useRef(0);

  // Helper to check valid access session
  const hasValidAccessSession = () => {
    const passcode = sessionStorage.getItem('BPA_SESSION_PASSCODE');
    const expiry = sessionStorage.getItem('BPA_SESSION_EXPIRY');
    return !!(passcode && expiry && Date.now() < parseInt(expiry, 10));
  };

  // Clear session and lock vault
  const clearSessionAndLock = () => {
    sessionStorage.removeItem('BPA_SESSION_PASSCODE');
    sessionStorage.removeItem('BPA_SESSION_EXPIRY');
    vaultUnlockedRef.current = false;
    setAuth({ token: null, expiry: 0 });
    setIsVaultUnlocked(false);
    setPages([]);
    setCategories([]);
    setSelectedCategory('All');
    setSyncStatus('offline');
    window.location.hash = '/';
  };

  const reconcileCategories = (localCategories, pagesData) => {
    let cats = [];
    if (Array.isArray(localCategories) && localCategories.length > 0) {
      cats = [...localCategories];
    } else {
      cats = [...DEFAULT_CATEGORIES];
    }
    const pageCategories = new Set();
    pagesData.forEach(page => {
      if (!page.deletedAt && page.category) pageCategories.add(String(page.category).trim());
    });
    pageCategories.forEach(cat => {
      const exists = cats.some(c => String(c).trim().toLowerCase() === String(cat).trim().toLowerCase());
      if (!exists && cat) cats.push(cat);
    });
    const uniqueCategories = [];
    const seen = new Set();
    cats.forEach(cat => {
      const trimmed = String(cat).trim();
      if (!trimmed) return;
      const lower = trimmed.toLowerCase();
      if (!seen.has(lower)) { seen.add(lower); uniqueCategories.push(trimmed); }
    });
    const otherIndex = uniqueCategories.findIndex(c => c.toLowerCase() === 'other');
    if (otherIndex === -1) uniqueCategories.push('Other');
    else if (otherIndex !== uniqueCategories.length - 1) { uniqueCategories.splice(otherIndex, 1); uniqueCategories.push('Other'); }
    return uniqueCategories;
  };

  const persistCategories = async (nextCategories, options = { sync: true }, toastFn) => {
    const cleaned = nextCategories.map(c => c.trim()).filter(c => c.length > 0).filter((c, idx, arr) => idx === arr.findIndex(other => other.toLowerCase() === c.toLowerCase()));
    const categoriesWithoutOther = cleaned.filter(c => c.toLowerCase() !== 'other');
    const finalOrder = [...categoriesWithoutOther, 'Other'];
    setCategories(finalOrder);
    localStorage.setItem('BPA_CATEGORIES', JSON.stringify(finalOrder));
    localStorage.setItem('BPA_CATEGORIES_UPDATED_AT', Date.now().toString());

    if (options.sync) {
      const sessionPasscode = sessionStorage.getItem('BPA_SESSION_PASSCODE');
      const sessionExpiry = sessionStorage.getItem('BPA_SESSION_EXPIRY');
      const hasValidPasscode = sessionPasscode && sessionExpiry && Date.now() < parseInt(sessionExpiry, 10);

      if (hasValidPasscode) {
        try {
          await dbAPI.saveSetting(
            GOOGLE_APPS_SCRIPT_WEB_APP_URL,
            'approvedCategoriesJson',
            {
              items: finalOrder,
              updatedAt: Date.now(),
              schemaVersion: 2
            },
            sessionPasscode
          );
        } catch (err) {
          console.error('Category sync error:', err);
          if (toastFn) toastFn('Category sync failed', 'info');
        }
      }
    }

    return finalOrder;
  };

  const safeRefreshFromCloud = async (silent = false, toastFn) => {
    try {
      const backupKey = `BPA_PRE_REFRESH_BACKUP_${Date.now()}`;
      const localData = JSON.parse(localStorage.getItem('BPA_HTML_Vault') || '[]');
      const localCategories = localStorage.getItem('BPA_CATEGORIES');
      localStorage.setItem(backupKey, JSON.stringify({
        documents: localData,
        categories: localCategories
      }));

      if (!silent && toastFn) toastFn('Creating backup...', 'info');

      const result = await dbAPI.syncWithRemote(GOOGLE_APPS_SCRIPT_WEB_APP_URL);

      if (result.status === 'synced') {
        const freshData = await dbAPI.getAll();
        setPages(freshData.sort((a, b) => b.updatedAt - a.updatedAt));

        const savedCategories = localStorage.getItem('BPA_CATEGORIES');
        if (savedCategories) {
          setCategories(JSON.parse(savedCategories));
        }

        if (!silent && toastFn) toastFn('Cloud refresh complete.', 'success');
      }
    } catch (error) {
      console.error('Safe refresh error:', error);
    }
  };

  const loadData = async () => {
    const data = await dbAPI.getAll();
    const loadedPages = data.sort((a, b) => b.updatedAt - a.updatedAt);

    const savedCategoriesRaw = localStorage.getItem('BPA_CATEGORIES');
    const savedCategories = savedCategoriesRaw ? JSON.parse(savedCategoriesRaw) : null;
    const reconciledCategories = reconcileCategories(savedCategories, loadedPages);

    setCategories(reconciledCategories);
    localStorage.setItem('BPA_CATEGORIES', JSON.stringify(reconciledCategories));
    localStorage.setItem('BPA_CATEGORIES_UPDATED_AT', Date.now().toString());

    setPages(loadedPages);
    setLoading(false);
  };

  const performSync = async (toastFn, options = {}) => {
    // Check for valid session before every sync
    if (!hasValidAccessSession()) {
      console.log('[Sync] Session expired or missing, locking vault');
      if (toastFn && typeof toastFn === 'function') {
        toastFn('Your session expired. Please log in again.', 'error');
      }
      clearSessionAndLock();
      return;
    }

    // Prevent overlapping syncs
    if (syncInProgressRef.current) {
      console.log('[Sync] Already running, queuing another sync');
      syncQueuedRef.current = true;
      return;
    }

    // Debounce rapid triggers (focus + visibility can fire together)
    const now = Date.now();
    if (options.trigger && options.trigger !== 'login' && options.trigger !== 'manual') {
      if (now - lastSyncTriggerRef.current < 5000) {
        console.log('[Sync] Debounced - too soon after last trigger');
        return;
      }
    }
    lastSyncTriggerRef.current = now;

    syncInProgressRef.current = true;
    setSyncStatus('syncing');
    console.log('[Sync] Starting sync process...');
    console.log('[Sync] Trigger reason:', options.trigger || 'manual');

    let retryCount = 0;
    const maxRetries = options.isAutomatic ? 2 : 0;

    const attemptSync = async () => {
      try {
        const result = await dbAPI.syncWithRemote(GOOGLE_APPS_SCRIPT_WEB_APP_URL);
        console.log('[Sync] Result:', result);

        if (result.status === 'synced') {
          console.log('[Sync] Success, reloading data');
          await loadData();
          setSyncStatus('synced');
          localStorage.setItem('BPA_LAST_SUCCESSFUL_SYNC_AT', Date.now().toString());
        } else if (result.status === 'error') {
          console.error('[Sync] Failed:', result.message);

          // Check for session expiry
          if (result.code === 'SESSION_REQUIRED') {
            if (toastFn && typeof toastFn === 'function') {
              toastFn('Your session expired. Please log in again.', 'error');
            }
            clearSessionAndLock();
            return;
          }

          // Retry transient errors for automatic sync
          if (options.isAutomatic && retryCount < maxRetries &&
              (result.code === 'NETWORK_ERROR' || result.code === 'SYNC_PULL_FAILED' || result.code === 'SYNC_PUSH_FAILED')) {
            retryCount++;
            const delay = retryCount * 3000; // 3s, 6s
            console.log(`[Sync] Retrying in ${delay}ms (attempt ${retryCount}/${maxRetries})`);
            if (toastFn && typeof toastFn === 'function') {
              toastFn('Sync will retry when the connection is restored.', 'info');
            }
            await new Promise(resolve => setTimeout(resolve, delay));
            return attemptSync();
          }

          setSyncStatus('error');
          if (toastFn && typeof toastFn === 'function') {
            if (result.code === 'NETWORK_ERROR') {
              toastFn('Sync will retry when the connection is restored.', 'info');
            } else {
              toastFn(`Sync failed: ${result.message}`, 'error');
            }
          }
        }
      } catch (error) {
        console.error('[Sync] Exception:', error);

        if (retryCount < maxRetries && options.isAutomatic) {
          retryCount++;
          const delay = retryCount * 3000;
          console.log(`[Sync] Retrying in ${delay}ms (attempt ${retryCount}/${maxRetries})`);
          await new Promise(resolve => setTimeout(resolve, delay));
          return attemptSync();
        }

        setSyncStatus('error');
        if (toastFn && typeof toastFn === 'function') {
          toastFn('Sync error occurred', 'error');
        }
      }
    };

    await attemptSync();

    syncInProgressRef.current = false;

    // Run queued sync if any
    if (syncQueuedRef.current) {
      console.log('[Sync] Running queued sync...');
      syncQueuedRef.current = false;
      await performSync(toastFn, options);
    }
  };

  // Clean up obsolete localStorage keys on startup
  useEffect(() => {
    localStorage.removeItem('BPA_SYNC_URL');
    localStorage.removeItem('BPA_SYNC_KEY');
    localStorage.removeItem('BPA_ALLOW_SYNC_URL_OVERRIDE');
  }, []);

  // Check vault access on mount - fail-safe startup
  useEffect(() => {
    let active = true;

    const checkAccess = async () => {
      try {
        if (hasValidAccessSession()) {
          vaultUnlockedRef.current = true;

          if (active) {
            setIsVaultUnlocked(true);
            setAuth({
              token: 'active',
              expiry: Date.now() + AUTH_SESSION_DURATION
            });
          }

          await loadData();

          const currentSchema = localStorage.getItem('BPA_APP_DATA_SCHEMA_VERSION');
          if (currentSchema !== APP_DATA_SCHEMA_VERSION) {
            await safeRefreshFromCloud(true);
            localStorage.setItem('BPA_APP_DATA_SCHEMA_VERSION', APP_DATA_SCHEMA_VERSION);
          }

          await performSync(undefined, {
            trigger: 'mount',
            isAutomatic: true
          });
        }
      } catch (error) {
        console.error(
          '[Startup] Vault initialization failed:',
          error instanceof Error ? error.message : 'Unknown startup error'
        );
        // Do not erase documents or browser data.
        // Do not leave the application permanently loading.
      } finally {
        if (active) {
          setLoading(false);
        }
      }
    };

    checkAccess();

    return () => {
      active = false;
    };
  }, []);

  // Auto-sync triggers
  useEffect(() => {
    if (!isVaultUnlocked) return;

    const handleOnline = () => {
      console.log('[Sync] Trigger: online');
      performSync(undefined, { trigger: 'online', isAutomatic: true });
    };

    const handleVisibility = () => {
      if (!document.hidden) {
        console.log('[Sync] Trigger: visible');
        performSync(undefined, { trigger: 'visible', isAutomatic: true });
      }
    };

    const handleFocus = () => {
      console.log('[Sync] Trigger: focus');
      performSync(undefined, { trigger: 'focus', isAutomatic: true });
    };

    // 2-minute interval sync
    const interval = setInterval(() => {
      if (isVaultUnlocked && hasValidAccessSession()) {
        console.log('[Sync] Trigger: interval');
        performSync(undefined, { trigger: 'interval', isAutomatic: true });
      }
    }, 2 * 60 * 1000);

    window.addEventListener('online', handleOnline);
    document.addEventListener('visibilitychange', handleVisibility);
    window.addEventListener('focus', handleFocus);

    return () => {
      window.removeEventListener('online', handleOnline);
      document.removeEventListener('visibilitychange', handleVisibility);
      window.removeEventListener('focus', handleFocus);
      clearInterval(interval);
    };
  }, [isVaultUnlocked]);

  const requestAuth = (onSuccess) => {
    const now = Date.now();
    const sessionPasscode = sessionStorage.getItem('BPA_SESSION_PASSCODE');
    const sessionExpiry = sessionStorage.getItem('BPA_SESSION_EXPIRY');
    const hasValidSession = sessionPasscode && sessionExpiry && now < parseInt(sessionExpiry, 10);

    if ((auth.token && now < auth.expiry) || hasValidSession) {
      onSuccess();
    } else {
      setPendingAction(() => onSuccess);
      setIsAuthModalOpen(true);
    }
  };

  const lockVault = () => {
    clearSessionAndLock();
  };

  if (loading) return <div className="h-screen w-full flex items-center justify-center bg-slate-50"><SafeIcon name="Loader" className="w-10 h-10 text-blue-600 animate-spin" /></div>;

  // Show vault access gate if not unlocked
  if (!isVaultUnlocked) {
    return (
      <ToastProvider>
        <AppContext.Provider value={{
          pages,
          categories,
          setCategories,
          syncStatus,
          requestAuth,
          selectedCategory,
          setSelectedCategory,
          savePage: async (p) => {
            await dbAPI.save(p);
            await loadData();
            await performSync(undefined, { trigger: 'save', isAutomatic: false });
          },
          deletePage: async (id) => {
            await dbAPI.softDelete(id);
            await loadData();
            await performSync(undefined, { trigger: 'delete', isAutomatic: false });
          },
          forceSync: performSync,
          loadData,
          exportBackup: () => {
            const blob = new Blob([JSON.stringify(pages, null, 2)], { type: 'application/json' });
            const a = document.createElement('a');
            a.href = URL.createObjectURL(blob);
            a.download = `bpa-vault-export.json`;
            a.click();
          },
          importBackup: async (data) => {
            for (const p of data) await dbAPI.save(p);
            await loadData();
          },
          persistCategories,
          safeRefreshFromCloud,
          lockVault,
          isVaultUnlocked,
          vaultUnlockedRef,
          viewMode,
          setViewMode
        }}>
          <VaultAccessGate onUnlock={async () => {
            vaultUnlockedRef.current = true;
            setIsVaultUnlocked(true);
            setAuth({ token: 'active', expiry: Date.now() + AUTH_SESSION_DURATION });
            setSyncStatus('syncing');
            await loadData();
            await performSync(undefined, { trigger: 'login', isAutomatic: false });
          }} />
        </AppContext.Provider>
      </ToastProvider>
    );
  }

  return (
    <ToastProvider>
      <AppContext.Provider value={{
        pages,
        categories,
        setCategories,
        syncStatus,
        requestAuth,
        selectedCategory,
        setSelectedCategory,
        savePage: async (p) => {
          await dbAPI.save(p);
          await loadData();
          await performSync(undefined, { trigger: 'save', isAutomatic: false });
        },
        deletePage: async (id) => {
          await dbAPI.softDelete(id);
          await loadData();
          await performSync(undefined, { trigger: 'delete', isAutomatic: false });
        },
        forceSync: performSync,
        loadData,
        exportBackup: () => {
          const blob = new Blob([JSON.stringify(pages, null, 2)], { type: 'application/json' });
          const a = document.createElement('a');
          a.href = URL.createObjectURL(blob);
          a.download = `bpa-vault-export.json`;
          a.click();
        },
        importBackup: async (data) => {
          for (const p of data) await dbAPI.save(p);
          await loadData();
        },
        persistCategories,
        safeRefreshFromCloud,
        lockVault,
        isVaultUnlocked,
        vaultUnlockedRef,
        viewMode,
        setViewMode
      }}>
        <HashRouter>
          <AppShell>
            <Routes>
              <Route path="/" element={<Dashboard />} />
              <Route path="/page/:id" element={<PageView />} />
            </Routes>
          </AppShell>
          <PasscodeModal
            isOpen={isAuthModalOpen}
            onClose={() => {
              setIsAuthModalOpen(false);
              setPendingAction(null);
            }}
            onVerified={() => {
              setAuth({ token: 'active', expiry: Date.now() + AUTH_SESSION_DURATION });
              if (pendingAction) pendingAction();
              setPendingAction(null);
            }}
          />
        </HashRouter>
      </AppContext.Provider>
    </ToastProvider>
  );
}